/**
 * Marketing → Organic SoMe → Overvågning ([D-383], Charlie 09-10-2026).
 *
 * Personer (typisk offentligt kendte, fx influencers) som marketing ønsker at
 * følge tæt: hvad gør de som næste handling? Deles af server (skema-værdier,
 * agentens regler, API-svar) og web (listen, drawers).
 *
 * ÆRLIGHED ([D-107]): Meta's API udstiller IKKE listen over hvem der følger
 * vores Instagram-/Facebook-profil — kun antallet. "Følger os" kan derfor kun
 * registreres manuelt eller afledes af engagement (kommentarer/omtaler på
 * vores egne opslag). Det skal UI'et sige, og agenten lover det aldrig.
 */

/**
 * 'overvaaget'  = på listen, hændelser spores hver nat.
 * 'foreslaaet'  = agenten har fundet personen; et menneske skal sige ja/nej.
 * 'udelukket'   = fjernet OG spærret: agenten foreslår personen aldrig igen.
 */
export const SOME_WATCH_STATUSES = ['overvaaget', 'foreslaaet', 'udelukket'] as const;
export type SomeWatchStatus = (typeof SOME_WATCH_STATUSES)[number];

/** Hvem satte personen på listen. */
export const SOME_WATCH_ORIGINS = ['manuel', 'agent'] as const;
export type SomeWatchOrigin = (typeof SOME_WATCH_ORIGINS)[number];

/**
 * Kvalificering af BC-matchet ([D-401]): et MENNESKE afgør om personen og den
 * koblede BC-kunde er samme person, efter selv at have slået navnet op
 * (siden tilbyder udgående søgelinks; appen henter og gemmer intet selv).
 * null = ikke kvalificeret endnu.
 */
export const SOME_WATCH_MATCH_VERDICTS = ['bekraeftet', 'afvist'] as const;
export type SomeWatchMatchVerdict = (typeof SOME_WATCH_MATCH_VERDICTS)[number];

/** De sociale platforme en persons profil kan ligge på (Charlies liste). */
export interface SomePlatformDef {
  key: string;
  label: string;
  /** Domænet profilen bor på — bruges til at aflede platform af en indsat URL. */
  host: string;
}

export const SOME_PLATFORMS: readonly SomePlatformDef[] = [
  { key: 'instagram', label: 'Instagram', host: 'instagram.com' },
  { key: 'facebook', label: 'Facebook', host: 'facebook.com' },
  { key: 'linkedin', label: 'LinkedIn', host: 'linkedin.com' },
  { key: 'tiktok', label: 'TikTok', host: 'tiktok.com' },
  { key: 'pinterest', label: 'Pinterest', host: 'pinterest.com' },
  // [D-392]: YouTube med, så berigelsen kan registrere kanaler (/@handle).
  { key: 'youtube', label: 'YouTube', host: 'youtube.com' },
];

export const SOME_PLATFORM_KEYS: readonly string[] = SOME_PLATFORMS.map((p) => p.key);

export function somePlatformLabel(key: string): string {
  return SOME_PLATFORMS.find((p) => p.key === key)?.label ?? key;
}

/**
 * Hvorfor personen står på listen (kan være flere — TagSelectInput-mønstret).
 * Det er PERSONENS relation til os ved tilføjelsen, ikke hændelsessporet.
 */
export interface SomeWatchReasonDef {
  key: string;
  label: string;
}

export const SOME_WATCH_REASONS: readonly SomeWatchReasonDef[] = [
  { key: 'foelger_some', label: 'Følger os på SoMe' },
  { key: 'nyhedsmail', label: 'Tilmeldt nyhedsmailen' },
  { key: 'tilbud', label: 'Har fået et tilbud' },
  { key: 'koeb', label: 'Har købt' },
];

export const SOME_WATCH_REASON_KEYS: readonly string[] = SOME_WATCH_REASONS.map((r) => r.key);

export function someWatchReasonLabel(key: string): string {
  return SOME_WATCH_REASONS.find((r) => r.key === key)?.label ?? key;
}

/**
 * Hændelsestyperne agenten sporer pr. nat. Kilden står på typen, så UI'et
 * altid kan nævne den ved navn (§30).
 *
 *   'tilbud'     = et salgstilbud er oprettet i Business Central.
 *   'ordre'      = en salgsordre er oprettet i Business Central (også webordrer).
 *   'koeb'       = en faktura er bogført i Business Central.
 *   'nyhedsmail' = personens email er dukket op i Klaviyo (tilmelding).
 */
export const SOME_WATCH_EVENT_TYPES = [
  { key: 'tilbud', label: 'Tilbud oprettet', source: 'BC' },
  { key: 'ordre', label: 'Ordre oprettet', source: 'BC' },
  { key: 'koeb', label: 'Køb faktureret', source: 'BC' },
  { key: 'nyhedsmail', label: 'Tilmeldt nyhedsmailen', source: 'Klaviyo' },
] as const;

export type SomeWatchEventType = (typeof SOME_WATCH_EVENT_TYPES)[number]['key'];

export function someWatchEventLabel(key: string): string {
  const def = SOME_WATCH_EVENT_TYPES.find((t) => t.key === key);
  return def ? def.label : key;
}

export function someWatchEventSource(key: string): string {
  return SOME_WATCH_EVENT_TYPES.find((t) => t.key === key)?.source ?? '';
}

/**
 * Afleder platform + brugernavn af en indsat profil-URL, fx
 * https://www.instagram.com/tippiemaya/ → { platform: 'instagram',
 * handle: 'tippiemaya' }. Null når URL'en ikke matcher en kendt platform —
 * platformen kan så vælges manuelt.
 */
export function parseSomeProfileUrl(url: string): { platform: string; handle: string | null } | null {
  const v = url.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
  const host = v.split('/')[0] ?? '';
  const platform = SOME_PLATFORMS.find((p) => host === p.host || host.endsWith(`.${p.host}`));
  if (!platform) return null;
  const path = v.slice(host.length).replace(/^\//, '');
  const first = path.split(/[/?#]/)[0] ?? '';
  // LinkedIn bruger /in/<navn>; YouTube-kanaler er /@handle (kanal-id'er på
  // /channel/… har intet håndtag); de øvrige har brugernavnet som første segment.
  const handle =
    platform.key === 'linkedin'
      ? (path.match(/^in\/([^/?#]+)/)?.[1] ?? null)
      : platform.key === 'youtube'
        ? (first.startsWith('@') ? first : null)
        : first || null;
  return { platform: platform.key, handle: handle ? decodeURIComponent(handle).replace(/^@/, '') : null };
}

// ---------------------------------------------------------------------------
// DTO'er
// ---------------------------------------------------------------------------

export interface SomeWatchProfileDto {
  id: number;
  platform: string;
  url: string;
  handle: string | null;
  /**
   * Følgertal ([D-392]): 'meta' = målt hos Meta (business_discovery),
   * 'web' = sprogmodellens websøgning (~-estimat, aldrig verificeret som
   * personens egen profil). null = intet følgertal kendt.
   */
  followerCount: number | null;
  followerCountAt: number | null;
  followerSource: 'meta' | 'web' | null;
  /** 'manuel' = tilføjet af et menneske; 'agent' = fundet af berigelsen. */
  origin: SomeWatchOrigin;
  createdAt: number;
  createdBy: string | null;
}

export interface SomeWatchEventDto {
  id: number;
  personId: number;
  eventType: string;
  /** Datakilden ved navn: 'BC' | 'Klaviyo'. */
  source: string;
  /** Dokumentnr./profil-id hos kilden — også idempotens-nøglen. */
  ref: string;
  /** Hvornår hændelsen skete hos kilden (ms); null når kilden kun oplyser en dato. */
  occurredAt: number | null;
  /** Kildens dato (ÅÅÅÅ-MM-DD) når klokkeslæt ikke findes. */
  occurredOn: string | null;
  /** Beløb i kr inkl. moms, hvor kilden har et (tilbud/ordre/faktura). */
  amountInclVatKr: number | null;
  summary: string;
  /** Historik: hændelsen lå før personen kom på listen og mailes aldrig. */
  baseline: boolean;
  detectedAt: number;
  mailedAt: number | null;
  mailError: string | null;
}

export interface SomeWatchPersonDto {
  id: number;
  name: string;
  /** BC-kundenummer (for private er kundenr = telefonnummer). */
  bcCustomerNo: string | null;
  email: string | null;
  phone: string | null;
  reasons: string[];
  note: string | null;
  status: SomeWatchStatus;
  origin: SomeWatchOrigin;
  suggestionReason: string | null;
  suggestionEvidence: Record<string, unknown> | null;
  createdAt: number;
  createdBy: string | null;
  decidedAt: number | null;
  decidedBy: string | null;
  excludedReason: string | null;
  /**
   * Berigelsen ([D-392]): hvad personen er kendt for (sprogmodellens
   * websøgning), hvornår berigelsen senest kørte, og evt. fejlen fra den.
   * enrichedAt sat + enrichError null = beriget; begge null = venter.
   */
  knownFor: string | null;
  enrichedAt: number | null;
  enrichError: string | null;
  /**
   * Kvalificering af BC-matchet ([D-401]): bekræftet/afvist af et menneske
   * med navn og tidspunkt; null = ikke kvalificeret. Nulstilles automatisk
   * hvis BC-kundenummeret senere ændres (dommen gjaldt det gamle match).
   */
  matchVerdict: SomeWatchMatchVerdict | null;
  matchDecidedBy: string | null;
  matchDecidedAt: number | null;
  matchNote: string | null;
  profiles: SomeWatchProfileDto[];
  /** Seneste hændelse (til listen); hele sporet hentes pr. person. */
  lastEvent: SomeWatchEventDto | null;
  eventCount: number;
}

export interface SomeWatchListResponse {
  ok: true;
  rows: SomeWatchPersonDto[];
  counts: Record<SomeWatchStatus, number>;
  /** Seneste hændelser på tværs af personer (nyeste først). */
  events: SomeWatchEventDto[];
  freshness: {
    /** Seneste agent-kørsel (hændelses-sporing), ms. */
    lastRunAt: number | null;
    /** Kildernes seneste synk (lokale spejl), ms. */
    bcSyncedAt: number | null;
    klaviyoSyncedAt: number | null;
  };
}
